/**
 * ═══════════════════════════════════════════════════════════════════════════
 * The kiosk tablet's screen must not fall asleep while the shop is armed
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Owner, 2026-09-13: "screen should not sleep when there is a kiosk mode."
 *
 * This is the Screen Wake Lock API, and nothing else in the estate held one —
 * there is no `WAKE_LOCK` permission in the Android manifest and no keep-awake
 * plugin. A web lock is the right layer for a kiosk that is a LIVE PAGE: it
 * works in Chrome on the tablet AND inside the G-38 shell, and it ships with
 * the web bundle instead of needing an APK rebuild.
 *
 * THE WHOLE REASON THIS IS A CONTROLLER AND NOT FOUR LINES IN A HOOK:
 * the browser RELEASES the lock on its own whenever the page stops being
 * visible — the screen timing out once, the florist switching apps, the shell
 * backgrounding. A naive `request()` at mount therefore works exactly once and
 * then silently never again, which looks identical to working. Re-acquiring on
 * `visibilitychange` is the entire mechanism, so it is the part that is unit
 * tested; a browser check cannot see it.
 *
 * It is also not a promise that the screen comes BACK on. A hidden document
 * cannot take a lock, so a tablet whose screen is already off stays off until
 * somebody touches it. This keeps a lit screen lit; it does not wake a dark one.
 */

/** The part of `WakeLockSentinel` we rely on, so tests can supply a fake. */
export interface WakeLockSentinelLike {
  release(): Promise<void>;
  addEventListener(type: "release", listener: () => void): void;
}

export interface ScreenWakeLockDeps {
  /** Resolves a fresh sentinel; rejects when the platform refuses (battery saver). */
  request: () => Promise<WakeLockSentinelLike>;
  /** A hidden document may not hold a lock, so asking while hidden is wasted. */
  isVisible: () => boolean;
  /** Fires whenever the lock is actually gained or lost — never guessed. */
  onHeldChange?: (held: boolean) => void;
}

/**
 * Holds a screen wake lock for as long as it is `start`ed, re-taking it every
 * time the platform takes it away.
 */
export class ScreenWakeLockController {
  private sentinel: WakeLockSentinelLike | null = null;
  private running = false;
  private held = false;
  /**
   * Bumped by every stop(). A request that was already in flight when the
   * controller stopped resolves into a stale generation and is released at
   * once, rather than leaving a lock nobody owns.
   */
  private generation = 0;
  private acquiring = false;

  constructor(private readonly deps: ScreenWakeLockDeps) {}

  isHeld(): boolean {
    return this.held;
  }

  /** Begin holding the lock, and keep holding it across visibility changes. */
  async start(): Promise<void> {
    if (this.running) return;
    this.running = true;
    await this.acquire();
  }

  /** Let the screen sleep again. Safe to call when never started. */
  async stop(): Promise<void> {
    this.running = false;
    this.generation += 1;
    const sentinel = this.sentinel;
    this.sentinel = null;
    this.setHeld(false);
    if (sentinel) {
      // A release that fails still leaves us with no reference to it; the
      // platform drops the lock with the document in every case that matters.
      await sentinel.release().catch(() => undefined);
    }
  }

  /**
   * Call on every `visibilitychange`. Becoming visible is the moment a lock the
   * browser quietly took away can be taken back.
   */
  async syncVisibility(): Promise<void> {
    if (!this.running) return;
    if (!this.deps.isVisible()) return;
    await this.acquire();
  }

  private async acquire(): Promise<void> {
    if (!this.running || this.sentinel || this.acquiring) return;
    if (!this.deps.isVisible()) return;

    const generation = this.generation;
    this.acquiring = true;
    try {
      const sentinel = await this.deps.request();
      if (!this.running || generation !== this.generation) {
        await sentinel.release().catch(() => undefined);
        return;
      }
      this.sentinel = sentinel;
      this.setHeld(true);
      // The platform's own release — the page was hidden, or battery saver
      // kicked in. Drop the reference so the next visibility change re-takes it.
      sentinel.addEventListener("release", () => {
        if (this.sentinel !== sentinel) return;
        this.sentinel = null;
        this.setHeld(false);
      });
    } catch {
      // Refused (battery saver, permissions policy). Not an error worth
      // showing mid-shift — `held` stays false and the next visibility
      // change tries again.
      this.setHeld(false);
    } finally {
      this.acquiring = false;
    }
  }

  private setHeld(next: boolean): void {
    if (this.held === next) return;
    this.held = next;
    this.deps.onHeldChange?.(next);
  }
}

/**
 * Browser-backed deps, or `null` where the API does not exist.
 *
 * `navigator.wakeLock` is typed non-optional by lib.dom, so TypeScript would
 * happily let us call it on a browser that has never heard of it — the runtime
 * check is load-bearing, not defensive decoration.
 */
export function browserScreenWakeLock(): Pick<ScreenWakeLockDeps, "request" | "isVisible"> | null {
  if (typeof navigator === "undefined" || !("wakeLock" in navigator)) return null;
  if (typeof document === "undefined") return null;
  return {
    request: () => navigator.wakeLock.request("screen"),
    isVisible: () => document.visibilityState === "visible",
  };
}
